// src/UserProfile/ProfileSettings.js
import React, { useEffect, useState } from "react";
import "./ProfileSettings.css";
import defaultProfile from "../EmptyProfile.jpg";

// Firebase
import { doc, setDoc, onSnapshot, getDoc } from "firebase/firestore";
import { getAuth, updatePassword, updateProfile } from "firebase/auth";
import { db } from "../../firebase";

/* Avatar URLs: use public/avatars/XX.png (可根據你圖檔總數自動產生) */
const BOY_AVATAR_URLS = Array.from({ length: 33 }, (_, i) =>
  `/avatars/boys${i + 1}.png`
);
const GIRL_AVATAR_URLS = Array.from({ length: 40 }, (_, i) =>
  `/avatars/girls${i + 31}.png`
);

export default function ProfileSettings() {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const [loading, setLoading] = useState(true);

  /* profile fields */
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [profilePic, setProfilePic] = useState("");
  const [password, setPassword] = useState("");
  const [feedback, setFeedback] = useState("");
  const [showGrid, setShowGrid] = useState(false);

  /* ① Live-listen to the profile document */
  useEffect(() => {
    if (!currentUser) return;
    const ref = doc(db, "users", currentUser.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        setFirstName(d.firstName || "");
        setLastName(d.lastName || "");
        setProfilePic(d.avatar || currentUser.photoURL || "");
      }
      setLoading(false);
    });
    return unsub;
  }, [currentUser]);

  /* ② Save avatar (always as public URL) */
  const saveAvatar = async (url) => {
    if (!currentUser) return;
    try {
      await setDoc(
        doc(db, "users", currentUser.uid),
        { avatar: url },
        { merge: true }
      );
      await updateProfile(currentUser, { photoURL: url });
      setProfilePic(url);
    } catch (err) {
      console.error("Avatar save failed:", err);
      setFeedback("Could not save avatar ― try again.");
    }
  };

  /* ③ Save all changes (names/password) */
  const handleSave = async () => {
    setFeedback("");
    if (!firstName.trim() || !lastName.trim()) {
      return setFeedback("First Name and Last Name cannot be empty.");
    }
    if (password && password.length < 6) {
      return setFeedback("Password must be at least 6 characters.");
    }
    if (!currentUser) return;
    const ref = doc(db, "users", currentUser.uid);

    try {
      await setDoc(
        ref,
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          avatar: profilePic || "",
        },
        { merge: true }
      );
      if (profilePic) await updateProfile(currentUser, { photoURL: profilePic });
      if (password) await updatePassword(currentUser, password);
      const fresh = await getDoc(ref);
      const userObj = { uid: currentUser.uid, ...fresh.data() };
      localStorage.setItem("loggedInUser", JSON.stringify(userObj));
      setFeedback("Profile updated successfully!");
      setPassword("");
    } catch (err) {
      console.error(err);
      setFeedback("Error updating profile. Try again.");
    }
  };

  /* ------------- render guards -------------- */
  if (!currentUser) {
    return (
      <div className="profile-settings-page">
        <h2>You are not logged in.</h2>
        <p>Please log in to access Profile Settings.</p>
      </div>
    );
  }
  if (loading) {
    return <p style={{ color: "white", textAlign: "center" }}>Loading…</p>;
  }

  /* ------------- UI -------------- */
  return (
    <div className="profile-settings-page">
      <div className="profile-settings-container">
        <h2 className="profile-settings-title">Profile Settings</h2>
        {feedback && <p className="profile-settings-feedback">{feedback}</p>}

        {/* Current avatar = click to show grid */}
        <div
          className="profile-settings-avatar-section"
          onClick={() => setShowGrid(!showGrid)}
        >
          <img
            src={profilePic || defaultProfile}
            alt="avatar"
            className="profile-settings-current-avatar"
          />
          <p className="profile-settings-tip">
            Click to {showGrid ? "hide" : "choose"} avatar
          </p>
        </div>

        {showGrid && (
          <div className="profile-settings-avatar-grid">
            <h3>Choose Your Avatar</h3>
            {/* boys */}
            <div className="avatar-row">
              {BOY_AVATAR_URLS.map((url, i) => (
                <img
                  key={`boy-${i + 1}`}
                  src={url}
                  alt={`boy avatar ${i + 1}`}
                  className={`profile-settings-avatar-thumbnail ${
                    profilePic === url ? "selected" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    saveAvatar(url); // Save as public URL!
                  }}
                />
              ))}
            </div>
            {/* girls */}
            <div className="avatar-row">
              {GIRL_AVATAR_URLS.map((url, i) => (
                <img
                  key={`girl-${i + 31}`}
                  src={url}
                  alt={`girl avatar ${i + 31}`}
                  className={`profile-settings-avatar-thumbnail ${
                    profilePic === url ? "selected" : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    saveAvatar(url);
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Names */}
        <div className="profile-settings-form-row">
          <label>First Name:</label>
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        </div>
        <div className="profile-settings-form-row">
          <label>Last Name:</label>
          <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>

        {/* Password */}
        <div className="profile-settings-form-row">
  <label>Change Password:</label>
  {/* Hidden field for password managers */}
  <input
    type="text"
    name="username"
    autoComplete="username"
    style={{ display: "none" }}
    tabIndex={-1}
    value={currentUser?.email || ""}
    readOnly
  />
  <input
    type="password"
    placeholder="Leave blank to keep existing password"
    value={password}
    onChange={(e) => setPassword(e.target.value)}
    autoComplete="new-password"
  />
</div>


        <button className="profile-settings-save-btn" onClick={handleSave}>
          Save Changes
        </button>
      </div>
    </div>
  );
}
